import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import BigNumber from "bignumber.js";

function formatCountdown(ms) {
    const total = Math.max(0, Math.ceil(Number(ms) / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatWhen(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatAmount(value) {
    const text = String(value == null ? "" : value).trim();
    if (!/^\d+(\.\d+)?$/.test(text)) return text;
    const [whole, frac = ""] = text.split(".");
    const cut = frac.slice(0, 8).replace(/0+$/, "");
    return cut ? `${whole}.${cut}` : whole;
}

function moneyLine(amount, asset) {
    if (amount == null || amount === "") return "—";
    const shown = formatAmount(amount);
    if (shown === "") return "—";
    return `${shown} ${asset || ""}`.trim();
}

/** Same cut the ledger uses: fee rounded down, net is the rest. */
function profitOnGet(getAmount, feePercent) {
    const grossText = String(getAmount || "").trim();
    const pctText = String(feePercent == null || feePercent === "" ? "0" : feePercent).trim();
    if (!/^\d+(\.\d+)?$/.test(grossText)) return { fee: "", net: "" };
    if (!/^\d+(\.\d+)?$/.test(pctText)) return { fee: "", net: grossText };
    const gross = new BigNumber(grossText);
    const pct = new BigNumber(pctText);
    if (!gross.isFinite() || gross.lte(0)) return { fee: "", net: "" };
    if (!pct.isFinite() || pct.lte(0)) return { fee: "0", net: grossText };
    const dot = grossText.indexOf(".");
    const places = dot === -1 ? 0 : grossText.length - dot - 1;
    const scale = Math.min(18, Math.max(8, places));
    const fee = gross.times(pct).div(100).decimalPlaces(scale, BigNumber.ROUND_DOWN);
    if (fee.lte(0)) return { fee: "0", net: grossText };
    const net = gross.minus(fee);
    if (net.lte(0)) return { fee: "", net: "" };
    const plain = (value) => value.toFixed(18).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
    return { fee: plain(fee), net: plain(net) };
}

function plainAmount(value) {
    return value.toFixed(18).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

/** Desk charge is the rate gap from the market mid, or a cut of the coin the user receives. */
function deskCharge(quote, requestRate) {
    const mid = new BigNumber(String(quote.reference_mid || requestRate || "").trim());
    const rate = new BigNumber(String(quote.rate || "").trim());
    const base = String(quote.pair_base || "").toUpperCase();
    const quoteAsset = String(quote.pair_quote || "").toUpperCase();
    const pay = String(quote.pay_asset || "").toUpperCase();
    const baseText = pay === base ? quote.pay_amount : quote.get_amount;
    const baseAmt = new BigNumber(String(baseText || "").trim());
    if (mid.isFinite() && mid.gt(0) && rate.isFinite() && rate.gt(0) && baseAmt.isFinite() && baseAmt.gt(0) && quoteAsset) {
        const charge = baseAmt.times(rate.minus(mid).abs());
        if (charge.gt(0)) return { amount: plainAmount(charge), asset: quoteAsset, percent: "" };
    }
    const split = profitOnGet(quote.get_amount, quote.fee_percent);
    const feePercent = String(quote.fee_percent == null ? "" : quote.fee_percent).trim();
    if (split.fee && split.fee !== "0") {
        return {
            amount: split.fee,
            asset: String(quote.get_asset || "").toUpperCase(),
            percent: feePercent,
        };
    }
    return null;
}

function DetailRow({ label, value, emphasize }) {
    return (
        <div className={`otc-accept-row${emphasize ? " is-emphasis" : ""}`}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}

export default function OtcAcceptQuoteModal({
    open,
    quote,
    rfq,
    countdownMs,
    submitting,
    kycPending,
    kycVerified,
    kycCtaLabel,
    onOpenKyc,
    limitError,
    onAccept,
    onClose,
}) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose]);

    if (!open || !quote || typeof document === "undefined") return null;

    const side = String(quote.side || rfq?.side || "").toUpperCase();
    const base = String(quote.pair_base || "").toUpperCase();
    const pairQuote = String(quote.pair_quote || "").toUpperCase();
    const pair = base && pairQuote ? `${base}/${pairQuote}` : "—";
    const profit = profitOnGet(quote.get_amount, quote.fee_percent);
    const requestRate = String(rfq?.request_rate || "").trim();
    const charge = deskCharge(quote, requestRate);
    const receiveAmount = profit.net && profit.fee && profit.fee !== "0" ? profit.net : quote.get_amount;
    const receive = moneyLine(receiveAmount, quote.get_asset);
    const quotedReceive = moneyLine(quote.get_amount, quote.get_asset);
    const chargeText = charge
        ? `${moneyLine(charge.amount, charge.asset)}${charge.percent && charge.percent !== "0" ? ` (${charge.percent}%)` : ""}`
        : "None";
    const rateText = formatAmount(quote.rate);
    const rate = rateText
        ? (base && pairQuote ? `${rateText} ${pairQuote} per 1 ${base}` : rateText)
        : "—";
    const expired = Number(countdownMs) <= 0;
    const sideClass = side === "SELL" ? "is-sell" : "is-buy";

    return createPortal(
        <div className="otc-accept-overlay" role="presentation">
            <div
                className="otc-accept-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="otc-accept-title"
            >
                <div className="otc-accept-head">
                    <div>
                        <p className="otc-accept-kicker">Desk quote</p>
                        <h2 id="otc-accept-title">Accept quote</h2>
                    </div>
                    <button type="button" className="otc-accept-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <p className="otc-accept-lead">
                    {side === "SELL"
                        ? `Sell ${pair}. You pay ${quote.pay_asset || ""} and receive ${quote.get_asset || ""}.`
                        : `Buy ${pair}. You pay ${quote.pay_asset || ""} and receive ${quote.get_asset || ""}.`}
                </p>

                <div className={`otc-accept-timer${expired ? " is-expired" : ""}`}>
                    {expired ? "This quote has expired" : `Expires in ${formatCountdown(countdownMs)}`}
                    {quote.expiresAt ? <span>{formatWhen(quote.expiresAt)}</span> : null}
                </div>

                <div className="otc-accept-rows">
                    <DetailRow label="Pair" value={pair} />
                    <DetailRow label="Side" value={side || "—"} />
                    <DetailRow label="You pay" value={moneyLine(quote.pay_amount, quote.pay_asset)} />
                    <DetailRow label="Rate" value={rate} />
                    {requestRate ? <DetailRow label="Your request rate" value={formatAmount(requestRate)} /> : null}
                    <DetailRow label="Quoted receive" value={quotedReceive} />
                    <DetailRow label="Spread in the rate" value={chargeText} />
                    <DetailRow label="You receive" value={receive} emphasize />
                </div>

                <p className="otc-accept-note">
                    Accepting locks the amount you pay until the desk settles this trade.
                </p>
                {limitError ? <p className="otc-accept-error">{limitError}</p> : null}

                <div className="otc-accept-actions">
                    {kycPending ? (
                        <button type="button" className={`otc-accept-submit ${sideClass}`} disabled>
                            Checking account…
                        </button>
                    ) : kycVerified ? (
                        <button
                            type="button"
                            className={`otc-accept-submit ${sideClass}`}
                            onClick={onAccept}
                            disabled={submitting || expired || Boolean(limitError)}
                        >
                            {submitting ? "Accepting…" : "Accept quote"}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={`otc-accept-submit ${sideClass}`}
                            onClick={() => {
                                // Close this popup first so the KYC modal is not hidden behind it.
                                onClose();
                                onOpenKyc?.();
                            }}
                        >
                            {kycCtaLabel || "Submit Kyc"}
                        </button>
                    )}
                    <button type="button" className="otc-accept-later" onClick={onClose} disabled={submitting}>
                        Not now
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
